import { IMPORT_TYPES } from '../../app/terminology/imports';
import { FEATURES } from '../../app/terminology/features';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getApiErrorMessage } from '../../app/api/error';
import agent, { AcesImportSetup, AtdPriceAdjustmentMode, CollectionMappingImportPreview, ConfiguratorAssignmentMode, CsvXlsxImportMode, CsvXlsxImportPreview, FitmentImportHistoryItem, ImportFormat, ProductTagFormatPreset, ProductTagImportPreview, ProductTagValidationMode } from '../../app/api/agent';
import { choiceValues, fieldChecked, fieldValue, useModalOpen, usePolarisEvents } from '../../app/components/polaris';
import { AtdProfile } from '../../app/models/Atd';
import { ProductGuideCollectionOption, ProductGuideSummary } from '../../app/models/ProductGuide';
import { useStore } from '../../app/stores/store';
import { isProductGuideEnabledForCurrentContext } from '../../app/utils/productGuideFeature';
import { useLegacyMode } from '../legacy/LegacyModeContext';
import ImportHistoryCards from './ImportHistoryCards';
import { downloadHandoffMessage, downloadImportFile } from './downloadImportFile';
import ProductTagReviewModal from './ProductTagReviewModal';
import { isPreparingTagImport, tagPreparationMessage } from './productTagImportUi';
import PlanFeatureStatus from '../plan/PlanFeatureStatus';
import type { PlanFeature } from '../plan/planPresentation';

type UploadState = {
    file?: File;
    error?: string;
    success?: string;
};

type AcesSetupState = {
    queueId: number;
    details: AcesImportSetup;
    selectedVendor: string;
    skuSuffixRule: string;
    useSkuSuffixRule: boolean;
    updateVendorRule: boolean;
    normalizeSku: boolean;
};

type VendorSkuRuleSettings = {
    skuPrefix: string;
    normalizeSku: boolean;
};

type ImportsPageView = 'imports' | 'exports';

const noVendorOptionValue = '__NO_VENDOR__';

type SelectableImportFormat = Exclude<ImportFormat, 'fitment_export'>;

type ImportFormatOption = {
    label: string;
    value: SelectableImportFormat;
};

const fitmentImportFormatOptions: ImportFormatOption[] = [
    { label: IMPORT_TYPES.csv_xlsx.label, value: 'csv_xlsx' },
    { label: IMPORT_TYPES.aces_xml.label, value: 'aces_xml' },
    { label: IMPORT_TYPES.product_tags.label, value: 'product_tags' },
];

const collectionMappingFormatOption: ImportFormatOption = {
    label: IMPORT_TYPES.collection_mappings_csv_xlsx.label,
    value: 'collection_mappings_csv_xlsx',
};

const configuratorDataFormatOption: ImportFormatOption = {
    label: IMPORT_TYPES.configurator_data_csv_xlsx.label,
    value: 'configurator_data_csv_xlsx',
};

const atdPriceFormatOption: ImportFormatOption = {
    label: IMPORT_TYPES.atd_price_file.label,
    value: 'atd_price_file',
};

const isPreviewedSpreadsheetFormat = (format: ImportFormat) =>
    format === 'csv_xlsx'
    || format === 'collection_mappings_csv_xlsx'
    || format === 'configurator_data_csv_xlsx';

const priceAdjustmentOptions = [
    { label: 'No adjustment', value: 'none' },
    { label: 'Increase by percentage', value: 'percent' },
    { label: 'Increase by fixed amount', value: 'fixed' },
];

const csvXlsxImportModeOptions = [
    { label: 'Add to existing fitments', value: 'append' },
    { label: 'Replace matched SKUs', value: 'replace_matched_skus' },
];

const configuratorAssignmentModeOptions = [
    { label: 'Update attributes and assign configurator', value: 'update_and_assign' },
    { label: 'Update attributes only', value: 'update_only' },
];

const productTagFormatOptions = [
    { label: 'App default tag format', value: 'app_default' },
    { label: 'Turn14 fits_ tag', value: 'turn14' },
    { label: 'Make-Model-Year', value: 'make_model_year' },
    { label: 'Make-Model-Submodel-Year', value: 'make_model_submodel_year' },
    { label: 'Year-Make-Model', value: 'year_make_model' },
    { label: 'Custom pattern', value: 'custom' },
];

const productTagValidationOptions = [
    { label: 'Include new and existing vehicles', value: 'create_missing' },
    { label: 'Existing vehicles only', value: 'existing_only' },
];

const importFormatIcons = {
    csv_xlsx: 'page',
    aces_xml: 'code',
    product_tags: 'product',
    collection_mappings_csv_xlsx: 'collection',
    configurator_data_csv_xlsx: 'settings',
    atd_price_file: 'note',
    turn14: 'link',
} as const;

const importPlanFeatures: Partial<Record<ImportFormat | 'turn14', PlanFeature>> = {
    csv_xlsx: 'spreadsheetImports', aces_xml: 'aces', product_tags: 'tagImports',
    collection_mappings_csv_xlsx: 'collectionImports', turn14: 'turn14', fitment_export: 'exports',
};

type ImportMethodRowProps = {
    label: string;
    format: SelectableImportFormat | 'turn14';
    lastUsed?: boolean;
    disabled?: boolean;
    onSelect: () => void;
};

const ImportMethodRow = ({ label, format, lastUsed, disabled, onSelect }: ImportMethodRowProps) => (
    <s-stack gap="none">
        <s-clickable onClick={onSelect} disabled={disabled} paddingBlock="small" paddingInline="small-100" borderRadius="base">
            <s-grid gridTemplateColumns="auto 1fr auto" gap="small" alignItems="center">
                <s-icon type={importFormatIcons[format]} />
                <s-stack direction="inline" gap="small-200" alignItems="center">
                    <s-text>{label}</s-text>
                    {lastUsed && <s-text color="subdued">Last used</s-text>}
                </s-stack>
                <s-icon type="chevron-right" color="subdued" />
            </s-grid>
        </s-clickable>
        {importPlanFeatures[format] && <PlanFeatureStatus feature={importPlanFeatures[format]!} />}
    </s-stack>
);

const formatFileSize = (size: number) => {
    if (size < 1024) {
        return `${size} bytes`;
    }

    if (size < 1024 * 1024) {
        return `${(size / 1024).toFixed(1)} KB`;
    }

    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const PreviewTable = ({ headings, rows }: { headings: string[]; rows: string[][] }) => (
    <s-table>
        <s-table-header-row>
            {headings.map((heading, index) => (
                <s-table-header key={heading} listSlot={index === 0 ? 'primary' : undefined}>{heading}</s-table-header>
            ))}
        </s-table-header-row>
        <s-table-body>
            {rows.map((row, rowIndex) => (
                <s-table-row key={rowIndex}>
                    {row.map((cell, cellIndex) => <s-table-cell key={cellIndex}>{cell || '-'}</s-table-cell>)}
                </s-table-row>
            ))}
        </s-table-body>
    </s-table>
);

const hasConnectedAtdPriceProfile = (profile: AtdProfile) => {
    if (!profile?.isActive) {
        return false;
    }

    // The profile endpoint returns a default unsaved profile, so require the
    // standard API credential fields that indicate a merchant actually connected ATD.
    return !!(
        profile.standardBaseUrl?.trim() &&
        profile.clientId?.trim() &&
        profile.username?.trim() &&
        profile.password?.trim() &&
        profile.locationNumber?.trim()
    );
};

const ImportsPage = () => {
    const navigate = useNavigate();
    const { userStore } = useStore();
    const { isLegacy } = useLegacyMode();
    const canUseConfigurators = isProductGuideEnabledForCurrentContext(userStore.user?.shopDomain);
    const [searchParams, setSearchParams] = useSearchParams();
    const requestedPageView: ImportsPageView = searchParams.get('view') === 'exports' ? 'exports' : 'imports';
    const pageView: ImportsPageView = isLegacy ? 'imports' : requestedPageView;
    const [importFormat, setImportFormat] = useState<ImportFormat>('csv_xlsx');
    const [showMoreImportOptions, setShowMoreImportOptions] = useState(false);
    const otherImportMethodsRef = useRef<HTMLDivElement>(null);
    const scrollToImportMethods = useRef(false);
    const [showTurn14Sources, setShowTurn14Sources] = useState(false);
    const [lastSuccessfulFormat, setLastSuccessfulFormat] = useState<ImportFormat | null>(null);
    const [uploadState, setUploadState] = useState<UploadState>({});
    const [exportState, setExportState] = useState<Pick<UploadState, 'error' | 'success'>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const submittingImport = useRef(false);
    const [importCapacity, setImportCapacity] = useState<{ activeCount: number; limit: number }>();
    const importQueueFull = !!importCapacity && importCapacity.activeCount >= importCapacity.limit;
    const importSubmissionDisabled = !importCapacity || importQueueFull;
    const importCapacityMessage = `You have ${importCapacity?.activeCount ?? 0} imports queued or running. Wait for one to finish before adding another.`;
    const [uploadProgressPercent, setUploadProgressPercent] = useState<number | undefined>();
    const [history, setHistory] = useState<FitmentImportHistoryItem[]>([]);
    const historyRequest = useRef(0);
    // An import/archive may finish after the user changes views. Refresh the current view.
    const reloadHistory = useRef<() => Promise<void>>(async () => {});
    const [historyError, setHistoryError] = useState<string | undefined>();
    const [historyErrorTitle, setHistoryErrorTitle] = useState<string | undefined>();
    const [downloadHandedOff, setDownloadHandedOff] = useState(false);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    // Polling reloads quietly once the current view has loaded.
    const [historyLoaded, setHistoryLoaded] = useState(false);
    const [archivingIds, setArchivingIds] = useState<Record<number, boolean>>({});
    const [downloadingIds, setDownloadingIds] = useState<Record<number, boolean>>({});
    const [downloadingReviewIds, setDownloadingReviewIds] = useState<Record<number, boolean>>({});
    const [isConfirmingAcesSetup, setIsConfirmingAcesSetup] = useState(false);
    const [acesSetup, setAcesSetup] = useState<AcesSetupState | undefined>();
    const [vendorSuffixRulesByVendor, setVendorSuffixRulesByVendor] = useState<Record<string, VendorSkuRuleSettings>>({});
    const [canUseAtdPriceFile, setCanUseAtdPriceFile] = useState(false);
    const [priceAdjustmentMode, setPriceAdjustmentMode] = useState<AtdPriceAdjustmentMode>('none');
    const [priceAdjustmentValue, setPriceAdjustmentValue] = useState('');
    const [skipZeroMapPrices, setSkipZeroMapPrices] = useState(true);
    const [productTagFormatPreset, setProductTagFormatPreset] = useState<ProductTagFormatPreset>('app_default');
    const [productTagCustomFormat, setProductTagCustomFormat] = useState('{make}-{model}-{year}');
    const [productTagValidationMode, setProductTagValidationMode] = useState<ProductTagValidationMode>('create_missing');
    const [productTagCatalogOnly, setProductTagCatalogOnly] = useState(false);
    const [productTagPreview, setProductTagPreview] = useState<ProductTagImportPreview | undefined>();
    const [productTagPreviewError, setProductTagPreviewError] = useState<string | undefined>();
    const [currentTagImport, setCurrentTagImport] = useState<FitmentImportHistoryItem>();
    const tagImportOutstanding = isPreparingTagImport(currentTagImport) || !!currentTagImport?.awaitingProductTagApproval;
    const [discardingTagPreview, setDiscardingTagPreview] = useState(false);
    const [confirmTagDiscard, setConfirmTagDiscard] = useState(false);
    const tagActionBusy = useRef(false);
    const openedTagReview = useRef<number | undefined>(undefined);
    const [reviewingProductTagIds, setReviewingProductTagIds] = useState<Record<number, boolean>>({});
    const [approvingProductTagIds, setApprovingProductTagIds] = useState<Record<number, boolean>>({});
    const tagPreviewBusy = discardingTagPreview || !!(productTagPreview && approvingProductTagIds[productTagPreview.queueId]);
    const [undoingProductTagIds, setUndoingProductTagIds] = useState<Record<number, boolean>>({});
    const [productTagUndoTarget, setProductTagUndoTarget] = useState<FitmentImportHistoryItem>();
    const [productTagUndoError, setProductTagUndoError] = useState<string>();
    const productTagUndoBusy = !!productTagUndoTarget && !!undoingProductTagIds[productTagUndoTarget.queueId];
    const csvPreviewRequest = useRef(0);
    const [customHeaderRow, setCustomHeaderRow] = useState(false);
    const [headerRowInput, setHeaderRowInput] = useState('');
    const [csvPreview, setCsvPreview] = useState<CsvXlsxImportPreview | CollectionMappingImportPreview | undefined>();
    const [csvPreviewFileKey, setCsvPreviewFileKey] = useState<string | undefined>();
    const [isCsvPreviewModalOpen, setIsCsvPreviewModalOpen] = useState(false);
    const [isPreviewingCsv, setIsPreviewingCsv] = useState(false);
    const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
    const [isQueueingExport, setIsQueueingExport] = useState(false);
    const queueingExport = useRef(false);
    const [hasActiveExport, setHasActiveExport] = useState<boolean>();
    const [csvXlsxImportMode, setCsvXlsxImportMode] = useState<CsvXlsxImportMode>('append');
    const [configurators, setConfigurators] = useState<ProductGuideSummary[]>([]);
    const [configuratorCollections, setConfiguratorCollections] = useState<ProductGuideCollectionOption[]>([]);
    const [selectedConfiguratorId, setSelectedConfiguratorId] = useState('');
    const [configuratorTemplateCollectionId, setConfiguratorTemplateCollectionId] = useState('');
    const [configuratorAssignmentMode, setConfiguratorAssignmentMode] = useState<ConfiguratorAssignmentMode>('update_and_assign');

    useEffect(() => {
        if (tagImportOutstanding && currentTagImport?.productTagCatalogOnly !== undefined)
            setProductTagCatalogOnly(currentTagImport.productTagCatalogOnly);
    }, [tagImportOutstanding, currentTagImport?.productTagCatalogOnly]);

    useEffect(() => {
        if (!isLegacy) {
            return;
        }

        setImportFormat('csv_xlsx');
    }, [isLegacy]);

    const importFormatOptions = useMemo(
        () => {
            const options = [...fitmentImportFormatOptions];
            if (canUseConfigurators) options.push(configuratorDataFormatOption);
            options.push(collectionMappingFormatOption);
            if (canUseAtdPriceFile) options.push(atdPriceFormatOption);
            return options;
        },
        [canUseAtdPriceFile, canUseConfigurators]
    );
    const selectedImportFormatOption = useMemo(
        () => importFormatOptions.find((option) => option.value === importFormat) ?? fitmentImportFormatOptions[0],
        [importFormat, importFormatOptions]
    );
    const toggleImportMethods = (scrollIntoView = false) => {
        scrollToImportMethods.current = scrollIntoView && !showMoreImportOptions;
        setShowMoreImportOptions(expanded => !expanded);
    };

    useEffect(() => {
        if (!showMoreImportOptions || !scrollToImportMethods.current) return;
        const frame = requestAnimationFrame(() => {
            scrollToImportMethods.current = false;
            otherImportMethodsRef.current?.scrollIntoView?.({
                behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
                block: 'nearest',
            });
        });
        return () => cancelAnimationFrame(frame);
    }, [showMoreImportOptions]);

    const handlePageViewChange = useCallback((value: ImportsPageView) => {
        const updatedSearchParams = new URLSearchParams(searchParams);

        if (value === 'exports') {
            updatedSearchParams.set('view', 'exports');
        } else {
            updatedSearchParams.delete('view');
        }

        setSearchParams(updatedSearchParams, { replace: true });
    }, [searchParams, setSearchParams]);

    const isValidFileForFormat = useCallback((file: File, format: ImportFormat) => {
        const extension = file.name.includes('.') ? file.name.split('.').pop()?.toLowerCase() : '';

        if (format === 'product_tags') {
            return true;
        }

        if (isPreviewedSpreadsheetFormat(format) || format === 'atd_price_file') {
            return extension === 'csv' || extension === 'xlsx';
        }

        return extension === 'xml';
    }, []);

    const getInvalidFileMessage = useCallback((format: ImportFormat) => {
        if (format === 'product_tags') {
            return 'Product Tags import does not require a file.';
        }

        if (format === 'aces_xml') {
            return 'Invalid file type. Select a .xml file.';
        }

        return 'Invalid file type. Select a .csv or .xlsx file.';
    }, []);

    const getFileKey = useCallback((file?: File) => {
        return file ? `${file.name}|${file.size}|${file.lastModified}` : undefined;
    }, []);

    const selectedFileKey = useMemo(() => getFileKey(uploadState.file), [getFileKey, uploadState.file]);
    // The preview must belong to the currently selected file; replacing the file
    // should always require a fresh mapping review before submission.
    const csvPreviewIsCurrent = isPreviewedSpreadsheetFormat(importFormat) && !!csvPreview && csvPreviewFileKey === selectedFileKey;
    const fitmentHeaderPreview = importFormat === 'csv_xlsx' ? csvPreview as CsvXlsxImportPreview | undefined : undefined;
    const headerRowOptions = [
        { label: 'Select the heading row', value: '', disabled: true },
        ...(fitmentHeaderPreview?.headerRows ?? []).map(row => ({
            label: 'Row ' + row.rowNumber + ': ' + (row.cells.filter(Boolean).join(' | ').slice(0, 180) || '(empty)'),
            value: String(row.rowNumber),
        })),
    ];
    if (fitmentHeaderPreview?.headerRowNumber && !headerRowOptions.some(option => option.value === String(fitmentHeaderPreview.headerRowNumber))) {
        headerRowOptions.push({ label: 'Row ' + fitmentHeaderPreview.headerRowNumber, value: String(fitmentHeaderPreview.headerRowNumber) });
    }
    headerRowOptions.push({ label: 'Enter another row number', value: 'custom' });
    const csvPreviewCanSubmit = !isPreviewingCsv && csvPreviewIsCurrent && csvPreview?.canImport === true
        && (importFormat !== 'csv_xlsx' || (!customHeaderRow && !!fitmentHeaderPreview?.headerRowNumber));

    useEffect(() => {
        let isMounted = true;

        agent.Atd.profile()
            .then((profile) => {
                if (isMounted) {
                    setCanUseAtdPriceFile(hasConnectedAtdPriceProfile(profile));
                }
            })
            .catch(() => {
                if (isMounted) {
                    setCanUseAtdPriceFile(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        if (!canUseAtdPriceFile && importFormat === 'atd_price_file') {
            setImportFormat('csv_xlsx');
        }
    }, [canUseAtdPriceFile, importFormat]);

    useEffect(() => {
        if (!canUseConfigurators) {
            setConfigurators([]);
            setConfiguratorCollections([]);
            return;
        }

        let isMounted = true;
        Promise.all([agent.ProductGuides.list(), agent.ProductGuides.collections()])
            .then(([guideResults, collectionResults]) => {
                if (!isMounted) return;
                const productConfigurators = guideResults.filter((guide) => guide.guideType === 'product-configurator');
                setConfigurators(productConfigurators);
                setConfiguratorCollections(collectionResults);
                setSelectedConfiguratorId((current) => current || (productConfigurators[0]?.id ? String(productConfigurators[0].id) : ''));
            })
            .catch(() => {
                if (!isMounted) return;
                setConfigurators([]);
                setConfiguratorCollections([]);
            });

        return () => {
            isMounted = false;
        };
    }, [canUseConfigurators]);

    const loadHistory = useCallback(async () => {
        const request = ++historyRequest.current;
        try {
            setIsLoadingHistory(true);
            setHistoryError(undefined);
            setHistoryErrorTitle(undefined);
            const [result, lastSuccessful, activeExport, capacity] = await Promise.all([
                agent.Imports.history('active', {
                    kind: isLegacy ? 'spreadsheets' : pageView,
                    limit: 6,
                }),
                !isLegacy && pageView === 'imports'
                    ? agent.Imports.lastSuccessful().catch(() => null)
                    : Promise.resolve(null),
                pageView === 'exports' ? agent.Imports.activeExport() : Promise.resolve(undefined),
                pageView === 'imports' ? agent.Imports.importCapacity() : Promise.resolve(undefined),
            ]);
            if (request !== historyRequest.current) return;
            setHistory(result);
            setHistoryLoaded(true);
            if (pageView === 'imports') setCurrentTagImport(current =>
                result.find(item => item.queueId === current?.queueId)
                ?? result.find(item => item.importFormat === 'product_tags' && (item.awaitingProductTagApproval || isPreparingTagImport(item)))
                ?? current);
            if (pageView === 'imports') setImportCapacity(capacity);
            if (pageView === 'exports') {
                setHasActiveExport(activeExport);
                if (activeExport === false) setExportState(prev => ({ ...prev, success: undefined }));
            }
            if (!isLegacy && pageView === 'imports') setLastSuccessfulFormat(lastSuccessful?.importFormat ?? null);
        } catch (error: any) {
            const message = await getApiErrorMessage(error, 'Failed to load import history.');
            if (request === historyRequest.current) setHistoryError(message);
        } finally {
            if (request === historyRequest.current) setIsLoadingHistory(false);
        }
    }, [isLegacy, pageView]);

    useEffect(() => {
        reloadHistory.current = loadHistory;
        setHistory([]);
        setHistoryLoaded(false);
        setHasActiveExport(undefined);
        setImportCapacity(undefined);
        loadHistory();
        return () => {
            historyRequest.current += 1;
            reloadHistory.current = async () => {};
        };
    }, [loadHistory]);

    useEffect(() => {
        const timer = window.setInterval(() => { void loadHistory(); }, 5000);
        return () => window.clearInterval(timer);
    }, [pageView, loadHistory]);

    useEffect(() => {
        if (!csvPreviewIsCurrent) {
            setIsCsvPreviewModalOpen(false);
        }
    }, [csvPreviewIsCurrent]);

    const loadVendorSuffixRules = useCallback(async () => {
        try {
            const rules = await agent.Settings.vendorSkuRules();
            const mappedRules = rules.reduce<Record<string, VendorSkuRuleSettings>>((accumulator, rule) => {
                accumulator[rule.shopifyVendor] = {
                    skuPrefix: rule.skuPrefix || '',
                    normalizeSku: rule.normalizeSku === true,
                };
                return accumulator;
            }, {});

            setVendorSuffixRulesByVendor(mappedRules);
        } catch {
            setVendorSuffixRulesByVendor({});
        }
    }, []);

    useEffect(() => {
        if (!acesSetup?.queueId) {
            return;
        }

        loadVendorSuffixRules();
    }, [acesSetup?.queueId, loadVendorSuffixRules]);

    useEffect(() => {
        if (!acesSetup?.selectedVendor) {
            return;
        }

        const vendorRule = vendorSuffixRulesByVendor[acesSetup.selectedVendor];

        if (vendorRule === undefined) {
            return;
        }

        setAcesSetup((previous) => {
            if (!previous || previous.selectedVendor !== acesSetup.selectedVendor || (previous.skuSuffixRule === vendorRule.skuPrefix && previous.normalizeSku === vendorRule.normalizeSku)) {
                return previous;
            }

            return {
                ...previous,
                skuSuffixRule: vendorRule.skuPrefix,
                normalizeSku: vendorRule.normalizeSku,
            };
        });
    }, [acesSetup?.selectedVendor, vendorSuffixRulesByVendor]);

    const handleFileSelect = useCallback((acceptedFiles: File[]) => {
        setUploadProgressPercent(undefined);
        csvPreviewRequest.current++;
        setIsPreviewingCsv(false);
        setCsvPreview(undefined);
        setCsvPreviewFileKey(undefined);
        setIsCsvPreviewModalOpen(false);
        const selectedFile = acceptedFiles[0];

        if (!selectedFile) {
            setUploadState({ file: undefined, error: 'No file selected.' });
            return;
        }

        if (!isValidFileForFormat(selectedFile, importFormat)) {
            setUploadState({
                file: undefined,
                success: undefined,
                error: getInvalidFileMessage(importFormat)
            });
            return;
        }

        setUploadState({ file: selectedFile, error: undefined, success: undefined });
    }, [getInvalidFileMessage, importFormat, isValidFileForFormat]);

    const handleRemoveSelectedFile = useCallback(() => {
        setUploadState({ file: undefined, error: undefined, success: undefined });
        setUploadProgressPercent(undefined);
        csvPreviewRequest.current++;
        setIsPreviewingCsv(false);
        setCsvPreview(undefined);
        setCsvPreviewFileKey(undefined);
        setIsCsvPreviewModalOpen(false);
    }, []);

    const handleFormatChange = useCallback((value: string) => {
        const normalized = value as ImportFormat;
        setImportFormat(normalized);
        setShowMoreImportOptions(false);
        setShowTurn14Sources(false);
        setAcesSetup(undefined);
        setUploadProgressPercent(undefined);
        csvPreviewRequest.current++;
        setIsPreviewingCsv(false);
        setCsvPreview(undefined);
        setCsvPreviewFileKey(undefined);
        setIsCsvPreviewModalOpen(false);
        setUploadState({ file: undefined, error: undefined, success: undefined });
    }, []);

    // Settings can link directly to the standard importer without creating a separate CSV workflow.
    useEffect(() => {
        if (searchParams.get('format') !== 'csv_xlsx' || pageView !== 'imports') return;
        handleFormatChange('csv_xlsx');
        const updatedSearchParams = new URLSearchParams(searchParams);
        updatedSearchParams.delete('format');
        setSearchParams(updatedSearchParams, { replace: true });
    }, [handleFormatChange, pageView, searchParams, setSearchParams]);

    const handleDownloadTemplate = useCallback(async () => {
        try {
            setIsDownloadingTemplate(true);
            const isCollectionMappingImport = importFormat === 'collection_mappings_csv_xlsx';
            const isConfiguratorDataImport = importFormat === 'configurator_data_csv_xlsx';
            const productGuideId = Number(selectedConfiguratorId);
            if (isConfiguratorDataImport && (!Number.isSafeInteger(productGuideId) || productGuideId <= 0)) {
                setUploadState((prev) => ({ ...prev, success: undefined, error: 'Select a configurator before downloading its template.' }));
                return;
            }

            const collectionId = Number(configuratorTemplateCollectionId);
            const response = isCollectionMappingImport
                ? await agent.Imports.collectionMappingTemplate()
                : isConfiguratorDataImport
                    ? await agent.Imports.configuratorDataTemplate(
                        productGuideId,
                        Number.isSafeInteger(collectionId) && collectionId > 0 ? collectionId : undefined)
                    : await agent.Imports.template();
            const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = isCollectionMappingImport
                ? 'collection-mapping-import-template.csv'
                : isConfiguratorDataImport
                    ? 'configurator-data-import-template.csv'
                    : 'fitment-import-template.csv';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error: any) {
            const message = await getApiErrorMessage(error, 'Unable to download import template.');
            setUploadState((prev) => ({ ...prev, success: undefined, error: message }));
        } finally {
            setIsDownloadingTemplate(false);
        }
    }, [configuratorTemplateCollectionId, importFormat, selectedConfiguratorId]);

    const handleQueueExport = useCallback(async () => {
        if (queueingExport.current || hasActiveExport !== false) return;
        queueingExport.current = true;
        try {
            setIsQueueingExport(true);
            setExportState((prev) => ({ ...prev, error: undefined }));
            await agent.Imports.exportFitments();
            setHasActiveExport(true);
            // Exports run through the history queue. The completed job exposes
            // the normal Download action once the worker has written the CSV.
            setExportState({
                error: undefined,
                success: 'Export started. The file will appear in export history when ready.'
            });
            await reloadHistory.current();
        } catch (error: any) {
            const message = await getApiErrorMessage(error, 'Unable to queue fitment export.');
            setExportState({ success: undefined, error: message });
        } finally {
            queueingExport.current = false;
            setIsQueueingExport(false);
        }
    }, [hasActiveExport]);

    const handlePreviewCsvXlsx = useCallback(async (headerRowNumber?: number) => {
        if (!uploadState.file) {
            setUploadState((prev) => ({ ...prev, error: 'Please select a file to upload.' }));
            return false;
        }

        if (!isPreviewedSpreadsheetFormat(importFormat) || !isValidFileForFormat(uploadState.file, importFormat)) {
            setUploadState((prev) => ({ ...prev, error: getInvalidFileMessage(importFormat) }));
            return false;
        }

        const requestId = ++csvPreviewRequest.current;
        try {
            setIsPreviewingCsv(true);
            setUploadState((prev) => ({ ...prev, error: undefined, success: undefined }));
            const productGuideId = Number(selectedConfiguratorId);
            if (importFormat === 'configurator_data_csv_xlsx'
                && (!Number.isSafeInteger(productGuideId) || productGuideId <= 0)) {
                setUploadState((prev) => ({ ...prev, error: 'Select a configurator before previewing the file.' }));
                return false;
            }

            // Each spreadsheet domain owns its preview so destination, variant,
            // and attribute validation can evolve independently.
            const preview = importFormat === 'collection_mappings_csv_xlsx'
                ? await agent.Imports.previewCollectionMappings(uploadState.file)
                : importFormat === 'configurator_data_csv_xlsx'
                    ? await agent.Imports.previewConfiguratorData(uploadState.file, productGuideId)
                    : await agent.Imports.previewCsvXlsx(uploadState.file, headerRowNumber);
            if (requestId !== csvPreviewRequest.current) return false;
            setCustomHeaderRow(false);
            setCsvPreview(preview);
            setCsvPreviewFileKey(getFileKey(uploadState.file));
            setIsCsvPreviewModalOpen(true);

            if (!preview.canImport) {
                setUploadState((prev) => ({ ...prev, error: 'Fix the CSV/XLSX column mapping before submitting this import.' }));
                return false;
            }

            return true;
        } catch (error: any) {
            if (requestId !== csvPreviewRequest.current) return false;
            const message = await getApiErrorMessage(error, 'Unable to preview CSV/XLSX import.');
            if (requestId !== csvPreviewRequest.current) return false;
            setCsvPreview(undefined);
            setCsvPreviewFileKey(undefined);
            setIsCsvPreviewModalOpen(false);
            setUploadState((prev) => ({ ...prev, success: undefined, error: message }));
            return false;
        } finally {
            if (requestId === csvPreviewRequest.current) setIsPreviewingCsv(false);
        }
    }, [getFileKey, getInvalidFileMessage, importFormat, isValidFileForFormat, selectedConfiguratorId, uploadState.file]);

    const handleSubmit = useCallback(async () => {
        if (submittingImport.current || importSubmissionDisabled) return;
        if (importFormat === 'product_tags' && tagImportOutstanding) return;
        if (importFormat !== 'product_tags' && !uploadState.file) {
            setUploadProgressPercent(undefined);
            setUploadState((prev) => ({ ...prev, error: 'Please select a file to upload.' }));
            return;
        }

        if (uploadState.file && !isValidFileForFormat(uploadState.file, importFormat)) {
            setUploadProgressPercent(undefined);
            setUploadState((prev) => ({
                ...prev,
                error: getInvalidFileMessage(importFormat)
            }));
            return;
        }

        if (isPreviewedSpreadsheetFormat(importFormat) && !csvPreviewCanSubmit) {
            await handlePreviewCsvXlsx();
            return;
        }

        const productGuideId = Number(selectedConfiguratorId);
        if (importFormat === 'configurator_data_csv_xlsx'
            && (!Number.isSafeInteger(productGuideId) || productGuideId <= 0)) {
            setUploadProgressPercent(undefined);
            setUploadState((prev) => ({ ...prev, error: 'Select a configurator before submitting the import.' }));
            return;
        }

        let parsedPriceAdjustmentValue: number | undefined;
        if (importFormat === 'atd_price_file' && priceAdjustmentMode !== 'none') {
            parsedPriceAdjustmentValue = Number(priceAdjustmentValue);

            if (!Number.isFinite(parsedPriceAdjustmentValue) || parsedPriceAdjustmentValue < 0) {
                setUploadProgressPercent(undefined);
                setUploadState((prev) => ({
                    ...prev,
                    error: 'Enter a price adjustment value greater than or equal to 0.'
                }));
                return;
            }
        }

        try {
            submittingImport.current = true;
            setIsSubmitting(true);
            setUploadProgressPercent(importFormat === 'product_tags' ? undefined : 0);
            const result = await agent.Imports.queue(
                uploadState.file,
                importFormat,
                (progressEvent) => {
                    if (!progressEvent.total || progressEvent.total <= 0) {
                        return;
                    }

                    const percent = Math.min(100, Math.round((progressEvent.loaded / progressEvent.total) * 100));
                    setUploadProgressPercent(percent);
                },
                importFormat === 'atd_price_file'
                    ? {
                        priceAdjustmentMode,
                        priceAdjustmentValue: parsedPriceAdjustmentValue,
                        skipZeroMapPrices,
                    }
                    : undefined,
                importFormat === 'product_tags'
                    ? {
                        productTagFormatPreset,
                        productTagCustomFormat: productTagFormatPreset === 'custom' ? productTagCustomFormat : undefined,
                        productTagValidationMode: productTagCatalogOnly ? 'existing_only' : productTagValidationMode,
                        productTagCatalogOnly,
                    }
                    : undefined,
                importFormat === 'csv_xlsx'
                    ? {
                        csvXlsxImportMode,
                        headerRowNumber: fitmentHeaderPreview?.headerRowNumber,
                    }
                    : undefined,
                importFormat === 'configurator_data_csv_xlsx'
                    ? {
                        productGuideId,
                        configuratorAssignmentMode,
                    }
                    : undefined
            );

            setImportCapacity(current => current ? { ...current, activeCount: current.activeCount + 1 } : current);

            if (importFormat === 'aces_xml' && result.acesSetup) {
                const details = result.acesSetup;
                setAcesSetup({
                    queueId: result.queueId,
                    details,
                    selectedVendor: details.matchedVendor || '',
                    skuSuffixRule: details.vendorSkuSuffixRule || '',
                    useSkuSuffixRule: true,
                    updateVendorRule: true,
                    normalizeSku: details.normalizeSku === true,
                });

                setUploadState({
                    file: undefined,
                    error: undefined,
                    success: 'ACES header parsed. Review vendor and SKU mapping settings, then start the import.'
                });
            } else {
                setUploadState({
                    file: undefined,
                    error: undefined,
                    success: importFormat === 'product_tags'
                        ? undefined
                        : importFormat === 'collection_mappings_csv_xlsx'
                            ? 'Your collection mapping import has been scheduled for processing.'
                            : importFormat === 'configurator_data_csv_xlsx'
                                ? 'Your configurator data import has been scheduled for processing.'
                            : 'Your import has been scheduled for processing.'
                });
            }

            if (importFormat === 'product_tags') setCurrentTagImport({
                queueId: result.queueId, importFormat: 'product_tags', state: 'Queued', addedOn: new Date().toISOString(),
                originalFileName: 'Shopify product tags', storedFileName: '', awaitingProductTagApproval: false,
                productTagCatalogOnly,
                isArchived: false, restrictToVendor: false, useVendorSkuSuffixRule: false, updateSavedVendorSkuSuffixRule: false,
            });
            csvPreviewRequest.current++;
            setIsPreviewingCsv(false);
            setCsvPreview(undefined);
            setCsvPreviewFileKey(undefined);
            setIsCsvPreviewModalOpen(false);
            await reloadHistory.current();
        } catch (error: any) {
            const message = await getApiErrorMessage(error, 'Failed to queue import.');
            setUploadState((prev) => ({ ...prev, success: undefined, error: message }));
            await reloadHistory.current();
        } finally {
            submittingImport.current = false;
            setIsSubmitting(false);
            setUploadProgressPercent(undefined);
        }
    }, [fitmentHeaderPreview?.headerRowNumber, configuratorAssignmentMode, csvPreviewCanSubmit, csvXlsxImportMode, getInvalidFileMessage, handlePreviewCsvXlsx, importFormat, importSubmissionDisabled, isValidFileForFormat, priceAdjustmentMode, priceAdjustmentValue, productTagCatalogOnly, productTagCustomFormat, productTagFormatPreset, productTagValidationMode, selectedConfiguratorId, skipZeroMapPrices, tagImportOutstanding, uploadState.file]);

    const handlePrimaryImportAction = useCallback(() => {
        if (isPreviewedSpreadsheetFormat(importFormat) && csvPreviewCanSubmit) {
            setIsCsvPreviewModalOpen(true);
            return;
        }

        handleSubmit();
    }, [csvPreviewCanSubmit, handleSubmit, importFormat]);

    const handleCloseCsvPreviewModal = useCallback(() => {
        if (isSubmitting) {
            return;
        }

        setIsCsvPreviewModalOpen(false);
    }, [isSubmitting]);

    const handleCancelCsvImport = useCallback(() => {
        if (isSubmitting) {
            return;
        }

        setUploadState({ file: undefined, error: undefined, success: undefined });
        setUploadProgressPercent(undefined);
        csvPreviewRequest.current++;
        setIsPreviewingCsv(false);
        setCsvPreview(undefined);
        setCsvPreviewFileKey(undefined);
        setIsCsvPreviewModalOpen(false);
    }, [isSubmitting]);

    const handleConfirmAcesSetup = useCallback(async () => {
        if (!acesSetup) {
            return;
        }

        try {
            setIsConfirmingAcesSetup(true);
            const hasVendorSelection = !!acesSetup.selectedVendor;
            await agent.Imports.configureAces(acesSetup.queueId, {
                selectedVendor: hasVendorSelection ? acesSetup.selectedVendor : undefined,
                skuSuffixRule: hasVendorSelection ? acesSetup.skuSuffixRule : undefined,
                useSkuSuffixRule: hasVendorSelection && acesSetup.useSkuSuffixRule,
                updateVendorRule: hasVendorSelection && acesSetup.updateVendorRule,
                normalizeSku: hasVendorSelection && acesSetup.normalizeSku,
            });

            setUploadState({ file: undefined, error: undefined, success: 'ACES import configured and queued for processing.' });
            setAcesSetup(undefined);
            setUploadProgressPercent(undefined);
            await reloadHistory.current();
        } catch (error: any) {
            const message = await getApiErrorMessage(error, 'Failed to configure ACES import.');
            setUploadState((prev) => ({ ...prev, success: undefined, error: message }));
        } finally {
            setIsConfirmingAcesSetup(false);
        }
    }, [acesSetup]);

    const handleCloseAcesModal = useCallback(() => {
        if (isConfirmingAcesSetup) {
            return;
        }

        setAcesSetup(undefined);
        setUploadProgressPercent(undefined);
    }, [isConfirmingAcesSetup]);

    const handleArchive = useCallback(async (queueId: number) => {
        try {
            setHistoryError(undefined);
            setHistoryErrorTitle(undefined);
            setArchivingIds((prev) => ({ ...prev, [queueId]: true }));
            await agent.Imports.archive(queueId);
            await reloadHistory.current();
        } catch (error: any) {
            setHistoryErrorTitle('Unable to archive history item');
            setHistoryError(await getApiErrorMessage(error, 'Failed to archive import history item.'));
        } finally {
            setArchivingIds((prev) => ({ ...prev, [queueId]: false }));
        }
    }, []);

    const handleDownload = useCallback(async (item: FitmentImportHistoryItem) => {
        try {
            setHistoryError(undefined);
            setHistoryErrorTitle(undefined);
            setDownloadingIds((prev) => ({ ...prev, [item.queueId]: true }));
            setDownloadHandedOff(false);
            await downloadImportFile(item.queueId);
            setDownloadHandedOff(true);
        } catch (error: any) {
            setHistoryErrorTitle('Unable to start file download');
            setHistoryError(await getApiErrorMessage(error, 'Failed to start file download.'));
        } finally {
            setDownloadingIds((prev) => ({ ...prev, [item.queueId]: false }));
        }
    }, []);

    const handleDownloadReview = useCallback(async (item: FitmentImportHistoryItem) => {
        try {
            setHistoryError(undefined);
            setHistoryErrorTitle(undefined);
            setDownloadingReviewIds((prev) => ({ ...prev, [item.queueId]: true }));
            const response = await agent.Imports.downloadReview(item.queueId);
            const blobUrl = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            const baseName = (item.originalFileName || `import-${item.queueId}`).replace(/\.[^/.]+$/, '');
            link.href = blobUrl;
            link.download = `${baseName}-review.csv`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (error: any) {
            setHistoryErrorTitle('Unable to download review file');
            setHistoryError(await getApiErrorMessage(error, 'Failed to download import review file.'));
        } finally {
            setDownloadingReviewIds((prev) => ({ ...prev, [item.queueId]: false }));
        }
    }, []);

    const handleReviewProductTags = useCallback(async (item: Pick<FitmentImportHistoryItem, 'queueId'>, discard = false) => {
        try {
            setHistoryError(undefined);
            setHistoryErrorTitle(undefined);
            setProductTagPreviewError(undefined);
            setReviewingProductTagIds((prev) => ({ ...prev, [item.queueId]: true }));
            const preview = await agent.Imports.productTagsPreview(item.queueId);
            setConfirmTagDiscard(discard && preview.awaitingApproval);
            setProductTagPreview(preview);
        } catch (error: any) {
            setHistoryErrorTitle('Unable to load Product Tags preview');
            setHistoryError(await getApiErrorMessage(error, 'Failed to load Product Tags preview.'));
        } finally {
            setReviewingProductTagIds((prev) => ({ ...prev, [item.queueId]: false }));
        }
    }, []);

    const requestedTagReview = Number(searchParams.get('reviewTagImport'));
    const requestedTagDiscard = searchParams.get('discard') === 'true';
    useEffect(() => {
        if (requestedTagReview > 0 && Number.isSafeInteger(requestedTagReview) && openedTagReview.current !== requestedTagReview) {
            openedTagReview.current = requestedTagReview;
            setImportFormat('product_tags');
            setShowTurn14Sources(false);
            void handleReviewProductTags({ queueId: requestedTagReview }, requestedTagDiscard);
        }
    }, [requestedTagReview, requestedTagDiscard, handleReviewProductTags]);

    const handleApproveProductTags = useCallback(async () => {
        if (!productTagPreview || !productTagPreview.awaitingApproval || !productTagPreview.willApplyRows || tagActionBusy.current) {
            return;
        }

        try {
            tagActionBusy.current = true;
            setProductTagPreviewError(undefined);
            setApprovingProductTagIds((prev) => ({ ...prev, [productTagPreview.queueId]: true }));
            const result = await agent.Imports.approveProductTags(productTagPreview.queueId);
            setUploadState({
                file: undefined,
                error: undefined,
                success: `Imported ${result.appliedFitmentCount.toLocaleString()} fitments${result.universalProductCount ? ` and ${result.universalProductCount.toLocaleString()} universal products` : ''}.`
            });
            setProductTagPreview(undefined);
            setCurrentTagImport(current => current?.queueId === productTagPreview.queueId
                ? { ...current, state: 'Applied', awaitingProductTagApproval: false } : current);
            await reloadHistory.current();
        } catch (error: any) {
            setProductTagPreviewError(await getApiErrorMessage(error, 'Failed to approve Product Tags import.'));
        } finally {
            tagActionBusy.current = false;
            setApprovingProductTagIds((prev) => ({ ...prev, [productTagPreview.queueId]: false }));
        }
    }, [productTagPreview]);

    const handleDiscardProductTags = async () => {
        if (!productTagPreview?.awaitingApproval || tagActionBusy.current) return;
        const queueId = productTagPreview.queueId;
        try {
            tagActionBusy.current = true;
            setDiscardingTagPreview(true);
            setProductTagPreviewError(undefined);
            await agent.Imports.discardProductTags(queueId);
            setProductTagPreview(undefined);
            setCurrentTagImport(current => current?.queueId === queueId
                ? { ...current, state: 'Discarded', awaitingProductTagApproval: false } : current);
            setUploadState({ success: 'Preview discarded. No fitments were changed.' });
            await reloadHistory.current();
        } catch (error) {
            setProductTagPreviewError(await getApiErrorMessage(error, 'Unable to discard this preview. Try again.'));
        } finally {
            tagActionBusy.current = false;
            setDiscardingTagPreview(false);
        }
    };

    const handleUndoProductTags = useCallback((item: FitmentImportHistoryItem) => {
        setProductTagUndoError(undefined);
        setProductTagUndoTarget(item);
    }, []);

    const confirmUndoProductTags = async () => {
        const item = productTagUndoTarget;
        if (!item || productTagUndoBusy) return;

        try {
            setProductTagUndoError(undefined);
            setHistoryError(undefined);
            setHistoryErrorTitle(undefined);
            setUndoingProductTagIds((prev) => ({ ...prev, [item.queueId]: true }));
            const result = await agent.Imports.undoProductTags(item.queueId);
            setUploadState({
                file: undefined,
                error: undefined,
                success: `Undid ${result.removedFitmentMappings.toLocaleString()} fitment mapping(s) from Product Tags import ${item.queueId}.`
            });
            setProductTagUndoTarget(undefined);
            await reloadHistory.current();
        } catch (error: any) {
            setProductTagUndoError(await getApiErrorMessage(error, 'Unable to undo this import. Try again.'));
        } finally {
            setUndoingProductTagIds((prev) => ({ ...prev, [item.queueId]: false }));
        }
    };

    const csvPreviewRows = csvPreviewIsCurrent
        ? (csvPreview?.rows ?? []).map((row) => {
            const status = importFormat === 'configurator_data_csv_xlsx'
                ? row.missingRequiredValues ? 'Missing required value' : 'Ready'
                : 'missingCollectionIdentifier' in row
                ? row.missingCollectionIdentifier
                    ? 'Missing collection'
                    : row.missingRequiredValues
                        ? 'Missing required value'
                        : row.emptyFitment
                            ? 'No vehicle values'
                            : row.invalidYearRange
                                ? 'Invalid year range'
                                : 'Ready'
                : 'invalidUniversal' in row && row.invalidUniversal
                    ? 'Invalid Universal value — will be skipped'
                    : 'missingUniversalIdentifier' in row && row.missingUniversalIdentifier
                        ? 'Missing product identifier — will be skipped'
                    : row.missingRequiredValues
                    ? 'Missing required value'
                    : row.emptyFitment
                        ? 'No fitment values'
                        : row.invalidYearRange
                            ? 'Invalid year range — will be skipped'
                        : 'universalOnly' in row && row.universalOnly
                            ? 'Universal status only — fitments preserved'
                        : !row.sku
                            ? ('variantId' in row && row.variantId ? 'Variant-level'
                                : row.productId || row.productHandle ? 'Product-level' : 'Vehicle only')
                            : 'Ready';

            return [
                String(row.rowNumber),
                ...row.cells,
                'expandedYearCount' in row && (row.expandedYearCount ?? 0) > 1
                    ? `${status} (${row.expandedYearCount} years)`
                    : status,
            ];
        })
        : [];
    const csvPreviewHeadings = csvPreviewIsCurrent
        ? ['Row', ...(csvPreview?.headings ?? []), 'Status']
        : [];
    const csvColumnMappingRows = csvPreviewIsCurrent
        ? (csvPreview?.mappedColumns ?? []).map((column) => [
            column.role === 'SKU' ? 'SKU' : column.fieldLabel,
            column.columnIndex >= 0 ? column.columnName || `Column ${column.columnIndex + 1}` : 'Not found',
            column.role.startsWith('Collection')
                ? 'At least one identifier required'
                : column.required && column.role === 'SelectorField' && csvPreview?.mappedColumns.some(mapped => mapped.role === 'Universal')
                    ? 'Required for vehicle rows'
                : column.required ? 'Required' : 'Optional',
        ])
        : [];
    const isCollectionMappingImport = importFormat === 'collection_mappings_csv_xlsx';
    const isConfiguratorDataImport = importFormat === 'configurator_data_csv_xlsx';
    const configuratorOptions = configurators.map((guide) => ({
        label: `${guide.name}${guide.isActive ? '' : ' (inactive)'}`,
        value: String(guide.id),
    }));
    const configuratorCollectionOptions = [
        { label: 'Currently assigned products (or blank template)', value: '' },
        ...configuratorCollections.map((collection) => ({
            label: collection.title,
            value: String(collection.id),
        })),
    ];
    const requiresFile = importFormat !== 'product_tags';
    const importFormBusy = isSubmitting || isPreviewingCsv || isConfirmingAcesSetup;
    const currentImportTypeLabel = showTurn14Sources ? 'Turn14 fitment data'
        : `${selectedImportFormatOption.label}${isPreviewedSpreadsheetFormat(importFormat) ? ' · CSV/Excel' : importFormat === 'aces_xml' ? ' · XML' : ''}`;
    const primaryImportActionLabel = importFormat === 'product_tags'
        ? 'Prepare preview'
        : isPreviewedSpreadsheetFormat(importFormat)
            ? 'Review import'
            : importFormat === 'aces_xml'
                ? 'Upload and continue'
                : 'Start import';
    const importHistory = useMemo(
        () => history.filter((item) =>
            item.importFormat !== 'fitment_export'
            && (!isLegacy || item.importFormat === 'csv_xlsx')),
        [history, isLegacy]
    );
    const exportHistory = useMemo(
        () => history.filter((item) => item.importFormat === 'fitment_export'),
        [history]
    );
    const visibleHistory = pageView === 'exports' ? exportHistory : importHistory;
    const historyTitle = pageView === 'exports' ? 'Export history' : 'Import history';
    const loadHistoryErrorTitle = pageView === 'exports' ? 'Unable to load export history' : 'Unable to load import history';
    const emptyHistoryText = pageView === 'exports' ? 'No export history yet.' : 'No import history yet.';

    const undoModalRef = useModalOpen(!!productTagUndoTarget, () => { if (!productTagUndoBusy) setProductTagUndoTarget(undefined); });
    const csvPreviewModalOpen = csvPreviewIsCurrent && isCsvPreviewModalOpen;
    const csvPreviewModalRef = useModalOpen(csvPreviewModalOpen, handleCloseCsvPreviewModal);
    const acesModalRef = useModalOpen(!!acesSetup, handleCloseAcesModal);
    const dropZoneEvents = usePolarisEvents({
        droprejected: () => {
            handleRemoveSelectedFile();
            setUploadState({ file: undefined, success: undefined, error: getInvalidFileMessage(importFormat) });
        },
    });

    const pageTitle = isLegacy ? 'Bulk Import' : pageView === 'exports' ? 'Exports' : 'Imports';
    const sectionTitle = showTurn14Sources ? 'Turn14 fitment data'
        : isLegacy || importFormat !== 'csv_xlsx' ? selectedImportFormatOption.label : 'Import fitments';

    const fileUpload = uploadState.file ? (
        <s-box border="base" borderRadius="base" padding="small-200 base">
            <s-grid gridTemplateColumns="auto 1fr auto" gap="small" alignItems="center">
                <s-icon type="page" />
                <s-stack gap="none">
                    <s-text fontWeight="semibold">{uploadState.file.name}</s-text>
                    <s-text color="subdued">{formatFileSize(uploadState.file.size)}</s-text>
                </s-stack>
                <s-button variant="tertiary" tone="critical" disabled={importFormBusy} onClick={handleRemoveSelectedFile}>Remove</s-button>
            </s-grid>
        </s-box>
    ) : (
        <s-drop-zone
            ref={dropZoneEvents}
            label="Import file"
            labelAccessibilityVisibility="exclusive"
            accessibilityLabel="Import file"
            accept={importFormat === 'aces_xml' ? '.xml' : '.csv,.xlsx'}
            disabled={importFormBusy}
            onChange={(event) => handleFileSelect(Array.from(event.currentTarget.files ?? []))}
        >
            <s-stack gap="small-200" alignItems="center" padding="large">
                <s-button variant="primary" icon="upload" disabled={importFormBusy}>Upload file</s-button>
                <s-text color="subdued">{importFormat === 'aces_xml' ? 'or drop an .xml file' : 'or drop a .csv or .xlsx file'}</s-text>
            </s-stack>
        </s-drop-zone>
    );

    const importMethods = !isLegacy && (
        <div ref={otherImportMethodsRef} role="region" aria-label="Other import methods" style={{ scrollMarginBlock: 20 }}>
            <s-stack gap="small-200">
                <s-divider />
                <s-stack direction="inline">
                    <s-button variant="tertiary" icon={showMoreImportOptions ? 'chevron-up' : 'chevron-down'}
                        aria-expanded={String(showMoreImportOptions)} aria-controls="pf-more-import-options"
                        disabled={importFormBusy} onClick={() => toggleImportMethods()}>
                        Other import methods
                    </s-button>
                </s-stack>
                <div id="pf-more-import-options" role="group" aria-label="Import type" hidden={!showMoreImportOptions}>
                    {showMoreImportOptions && (
                        <s-query-container>
                            <s-grid gridTemplateColumns="@container (inline-size > 640px) 1fr 1fr, 1fr" gap="none base">
                                {importFormatOptions.filter(option => showTurn14Sources || option.value !== importFormat).map(option => (
                                    <ImportMethodRow key={option.value} label={option.label} format={option.value}
                                        lastUsed={lastSuccessfulFormat === option.value} disabled={importFormBusy}
                                        onSelect={() => handleFormatChange(option.value)} />
                                ))}
                                {!showTurn14Sources && (
                                    <ImportMethodRow label="Turn14 fitment data" format="turn14" disabled={importFormBusy}
                                        onSelect={() => { setShowTurn14Sources(true); setShowMoreImportOptions(false); }} />
                                )}
                            </s-grid>
                        </s-query-container>
                    )}
                </div>
            </s-stack>
        </div>
    );

    const turn14Sources = (
        <s-box border="base" borderRadius="base">
            <s-box padding="base">
                <s-grid gridTemplateColumns="auto 1fr" gap="small" alignItems="start">
                    <s-icon type="page" />
                    <s-stack gap="small-200">
                        <s-heading>CSV from Data Here-to-There</s-heading>
                        <s-stack direction="inline">
                            <s-button onClick={() => handleFormatChange('csv_xlsx')}>Import CSV</s-button>
                        </s-stack>
                    </s-stack>
                </s-grid>
            </s-box>
            <s-divider />
            <s-box padding="base">
                <s-grid gridTemplateColumns="auto 1fr" gap="small" alignItems="start">
                    <s-icon type="link" />
                    <s-stack gap="small-200">
                        <s-heading>Direct Turn14 connection</s-heading>
                        <s-stack direction="inline">
                            <s-button onClick={() => navigate('/storefront?section=reference-data&connect=turn14')}>Set up connection</s-button>
                        </s-stack>
                    </s-stack>
                </s-grid>
            </s-box>
        </s-box>
    );

    const spreadsheetOptions = isPreviewedSpreadsheetFormat(importFormat) && (importFormat !== 'csv_xlsx' || isLegacy || !!uploadState.file) && (
        isCollectionMappingImport ? null : isConfiguratorDataImport ? (
            <s-stack gap="base">
                {configuratorOptions.length > 0 ? (
                    <s-select
                        label="Configurator"
                        value={selectedConfiguratorId}
                        onChange={(event) => {
                            setSelectedConfiguratorId(fieldValue(event));
                            csvPreviewRequest.current++;
                            setIsPreviewingCsv(false);
                            setCsvPreview(undefined);
                            setCsvPreviewFileKey(undefined);
                            setIsCsvPreviewModalOpen(false);
                        }}
                    >
                        {configuratorOptions.map(option => <s-option key={option.value} value={option.value}>{option.label}</s-option>)}
                    </s-select>
                ) : (
                    <s-banner tone="warning" heading="Create a configurator first">
                        No product configurators are available. Create one under {FEATURES.configurators}, connect its questions to variant metafields, then return here.
                    </s-banner>
                )}
                <s-select
                    label="Template product scope"
                    value={configuratorTemplateCollectionId}
                    disabled={configuratorOptions.length === 0}
                    onChange={(event) => setConfiguratorTemplateCollectionId(fieldValue(event))}
                >
                    {configuratorCollectionOptions.map(option => <s-option key={option.value} value={option.value}>{option.label}</s-option>)}
                </s-select>
                <s-choice-list
                    label="Import behavior"
                    values={[configuratorAssignmentMode]}
                    onChange={(event) => setConfiguratorAssignmentMode(choiceValues(event)[0] as ConfiguratorAssignmentMode)}
                >
                    {configuratorAssignmentModeOptions.map(option => <s-choice key={option.value} value={option.value}>{option.label}</s-choice>)}
                </s-choice-list>
            </s-stack>
        ) : (
            <s-choice-list
                label="Import behavior"
                values={[csvXlsxImportMode]}
                onChange={(event) => setCsvXlsxImportMode(choiceValues(event)[0] as CsvXlsxImportMode)}
            >
                {csvXlsxImportModeOptions.map(option => <s-choice key={option.value} value={option.value}>{option.label}</s-choice>)}
            </s-choice-list>
        )
    );

    const atdOptions = importFormat === 'atd_price_file' && (
        <s-stack gap="base">
            <s-select label="Price adjustment" value={priceAdjustmentMode}
                onChange={(event) => setPriceAdjustmentMode(fieldValue(event) as AtdPriceAdjustmentMode)}>
                {priceAdjustmentOptions.map(option => <s-option key={option.value} value={option.value}>{option.label}</s-option>)}
            </s-select>
            {priceAdjustmentMode !== 'none' && (
                <s-number-field
                    label={priceAdjustmentMode === 'percent' ? 'Increase MAP by' : 'Increase MAP by fixed amount'}
                    value={priceAdjustmentValue}
                    min={0}
                    autocomplete="off"
                    prefix={priceAdjustmentMode === 'percent' ? undefined : '$'}
                    suffix={priceAdjustmentMode === 'percent' ? '%' : undefined}
                    onInput={(event) => setPriceAdjustmentValue(fieldValue(event))}
                />
            )}
            <s-checkbox
                label="Skip price updates where MAP is blank or zero"
                checked={skipZeroMapPrices}
                onChange={(event) => setSkipZeroMapPrices(fieldChecked(event))}
            />
        </s-stack>
    );

    const tagOptionsDisabled = tagImportOutstanding || isSubmitting;
    const tagOptions = (
        <s-stack gap="base">
            <s-select label="Tag format" value={productTagFormatPreset} disabled={tagOptionsDisabled}
                onChange={(event) => setProductTagFormatPreset(fieldValue(event) as ProductTagFormatPreset)}>
                {productTagFormatOptions.map(option => <s-option key={option.value} value={option.value}>{option.label}</s-option>)}
            </s-select>
            {productTagFormatPreset === 'custom' && (
                <s-text-field
                    label="Custom tag pattern"
                    value={productTagCustomFormat}
                    disabled={tagOptionsDisabled}
                    autocomplete="off"
                    details="Tokens: {make}, {model}, {submodel}, {year}"
                    onInput={(event) => setProductTagCustomFormat(fieldValue(event))}
                />
            )}
            <s-checkbox
                label="Match against my vehicle catalog only"
                checked={productTagCatalogOnly}
                disabled={tagOptionsDisabled}
                onChange={(event) => setProductTagCatalogOnly(fieldChecked(event))}
            />
            {!productTagCatalogOnly && (
                <s-select label="Vehicles to include" value={productTagValidationMode} disabled={tagOptionsDisabled}
                    onChange={(event) => setProductTagValidationMode(fieldValue(event) as ProductTagValidationMode)}>
                    {productTagValidationOptions.map(option => <s-option key={option.value} value={option.value}>{option.label}</s-option>)}
                </s-select>
            )}
        </s-stack>
    );

    const tagStatus = importFormat === 'product_tags' && currentTagImport && tagImportOutstanding && (
        <s-banner
            tone={currentTagImport.awaitingProductTagApproval ? 'success' : currentTagImport.processingStage === 'blocked_shopify_sync' ? 'warning' : 'info'}
            heading={currentTagImport.awaitingProductTagApproval ? 'Your preview is ready' : currentTagImport.processingStage === 'blocked_shopify_sync' ? 'Preview preparation needs attention' : 'Preparing your preview'}
        >
            {currentTagImport.awaitingProductTagApproval ? 'Review the vehicle matches, then import them or discard this preview.' : tagPreparationMessage(currentTagImport)}
            {currentTagImport.awaitingProductTagApproval && (
                <s-button slot="secondary-actions" onClick={() => handleReviewProductTags(currentTagImport)}
                    loading={!!reviewingProductTagIds[currentTagImport.queueId]}>Review preview</s-button>
            )}
            {currentTagImport.processingStage === 'blocked_shopify_sync' && (
                <s-button slot="secondary-actions" href="/storefront?section=data-refresh">Check Shopify connection</s-button>
            )}
        </s-banner>
    );

    const importForm = <>
        {importFormat !== 'product_tags' ? <>
            {fileUpload}
            {isPreviewedSpreadsheetFormat(importFormat) && (
                <s-stack direction="inline" justifyContent="end">
                    <s-button variant="tertiary" icon="download" onClick={handleDownloadTemplate} loading={isDownloadingTemplate}
                        disabled={isConfiguratorDataImport && configuratorOptions.length === 0}>
                        Download CSV template
                    </s-button>
                </s-stack>
            )}
            {spreadsheetOptions}
            {atdOptions}
            {isPreviewedSpreadsheetFormat(importFormat) && csvPreviewIsCurrent && (
                <s-banner tone={csvPreview?.canImport ? 'success' : 'warning'}
                    heading={csvPreview?.canImport ? 'Import preview ready' : 'Import preview needs attention'}>
                    {csvPreview?.canImport ? 'Review the mapped columns and sample rows before submitting.' : 'Review the heading row and mapping errors before submitting.'}
                    <s-button slot="secondary-actions" onClick={() => setIsCsvPreviewModalOpen(true)}>Review preview</s-button>
                    <s-button slot="secondary-actions" tone="critical" onClick={handleCancelCsvImport} disabled={isSubmitting}>Cancel import</s-button>
                </s-banner>
            )}
        </> : tagOptions}

        {tagStatus}

        {importFormat === 'product_tags' && currentTagImport?.state.toLowerCase() === 'failed' && (
            <s-banner tone="critical" heading="We couldn’t prepare your preview">
                No fitments were changed. You can try preparing a new preview.
            </s-banner>
        )}

        {uploadState.error && <s-banner tone="critical" heading="Import validation failed">{uploadState.error}</s-banner>}
        {uploadState.success && <s-banner tone="success" heading="Import status">{uploadState.success}</s-banner>}

        {isSubmitting && uploadProgressPercent !== undefined && (
            <s-stack gap="small-100">
                <s-text color="subdued">Uploading file… {uploadProgressPercent}%</s-text>
                <s-progress accessibilityLabel="Upload progress" value={uploadProgressPercent} max={100} />
            </s-stack>
        )}

        {(!requiresFile || uploadState.file) && !(importFormat === 'product_tags' && tagImportOutstanding) && (
            <s-stack direction="inline" justifyContent="end">
                <s-button
                    variant="primary"
                    onClick={handlePrimaryImportAction}
                    loading={isSubmitting || isPreviewingCsv}
                    disabled={importSubmissionDisabled || isSubmitting || isPreviewingCsv
                        || (isPreviewedSpreadsheetFormat(importFormat) && csvPreviewIsCurrent && csvPreview?.canImport === false)
                        || (isConfiguratorDataImport && !selectedConfiguratorId)}
                >
                    {primaryImportActionLabel}
                </s-button>
            </s-stack>
        )}
    </>;

    const importSection = (
        <s-section accessibilityLabel="Import">
            <s-stack gap="base">
                {importQueueFull && <s-banner tone="info" heading="Import queue full">{importCapacityMessage}</s-banner>}
                <s-query-container>
                    <s-grid gridTemplateColumns="@container (inline-size > 480px) 1fr auto, 1fr" gap="small-200" alignItems="center" justifyItems="start">
                        <s-heading>{sectionTitle}</s-heading>
                        {!isLegacy && (
                            <s-button variant="tertiary" icon={showMoreImportOptions ? 'chevron-up' : 'chevron-down'} disabled={importFormBusy}
                                aria-expanded={String(showMoreImportOptions)} aria-controls="pf-more-import-options"
                                onClick={() => toggleImportMethods(true)}>
                                {currentImportTypeLabel}
                            </s-button>
                        )}
                    </s-grid>
                </s-query-container>
                {!showTurn14Sources && importPlanFeatures[importFormat] && <PlanFeatureStatus feature={importPlanFeatures[importFormat]!} />}
                {!isLegacy && showTurn14Sources ? turn14Sources : importForm}
                {importMethods}
            </s-stack>
        </s-section>
    );

    const exportSection = (
        <s-section accessibilityLabel="Fitment export">
            <s-stack gap="base">
                <s-grid gridTemplateColumns="1fr auto" gap="small-200" alignItems="center">
                    <s-heading>Fitment export</s-heading>
                    <s-button variant="primary" onClick={handleQueueExport} loading={isQueueingExport}
                        disabled={hasActiveExport !== false}>Export fitments</s-button>
                </s-grid>
                {exportState.error && <s-banner tone="critical" heading="Export failed">{exportState.error}</s-banner>}
                {exportState.success && <s-banner tone="success" heading="Export queued">{exportState.success}</s-banner>}
                {hasActiveExport && <s-text color="subdued">An export is queued or running. You can start another when it finishes.</s-text>}
            </s-stack>
        </s-section>
    );

    const historySection = (
        <s-section accessibilityLabel={historyTitle} padding="none">
            <s-box padding="base">
                <s-grid gridTemplateColumns="1fr auto" gap="small-200" alignItems="center">
                    <s-heading>{historyTitle}</s-heading>
                    <s-button variant="tertiary" icon="refresh" onClick={loadHistory} loading={isLoadingHistory}>Refresh</s-button>
                </s-grid>
            </s-box>

            {(downloadHandedOff || historyError) && (
                <s-box paddingInline="base" paddingBlockEnd="base">
                    <s-stack gap="base">
                        {downloadHandedOff && <s-text color="subdued">{downloadHandoffMessage}</s-text>}
                        {historyError && (
                            <s-banner tone="critical" heading={historyErrorTitle || loadHistoryErrorTitle}>{historyError}</s-banner>
                        )}
                    </s-stack>
                </s-box>
            )}

            {visibleHistory.length === 0 && (historyLoaded || !isLoadingHistory) ? (
                <s-box paddingInline="base" paddingBlockEnd="base">
                    <s-text color="subdued">{emptyHistoryText}</s-text>
                </s-box>
            ) : <>
                <ImportHistoryCards
                    items={visibleHistory.slice(0, 5)}
                    archivingIds={archivingIds}
                    downloadingIds={downloadingIds}
                    downloadingReviewIds={downloadingReviewIds}
                    reviewingProductTagIds={reviewingProductTagIds}
                    undoingProductTagIds={undoingProductTagIds}
                    onArchive={handleArchive}
                    onDownload={handleDownload}
                    onDownloadReview={handleDownloadReview}
                    onReviewProductTags={handleReviewProductTags}
                    onDiscardProductTags={item => handleReviewProductTags(item, true)}
                    onUndoProductTags={handleUndoProductTags}
                />
                <s-divider />
                <s-box padding="small-200 base">
                    <s-grid gridTemplateColumns="1fr auto" gap="small-200" alignItems="center">
                        <s-text color="subdued">
                            {isLoadingHistory && visibleHistory.length === 0 ? 'Loading history…' : visibleHistory.length > 5 ? 'Showing latest 5 runs' : `${visibleHistory.length} ${visibleHistory.length === 1 ? 'run' : 'runs'}`}
                        </s-text>
                        <s-button variant="tertiary" icon="clock"
                            onClick={() => navigate(pageView === 'exports' ? '/imports/history?view=exports' : '/imports/history')}>
                            View all history
                        </s-button>
                    </s-grid>
                </s-box>
            </>}
        </s-section>
    );

    const csvPreviewTitle = csvPreview?.canImport
        ? isCollectionMappingImport
            ? 'Review collection mapping import'
            : isConfiguratorDataImport ? 'Review configurator data import' : 'Review CSV/XLSX import'
        : isCollectionMappingImport
            ? 'Collection mapping preview needs attention'
            : isConfiguratorDataImport ? 'Configurator data preview needs attention' : 'CSV/XLSX preview needs attention';
    const csvSubmitLabel = isCollectionMappingImport
        ? 'Submit collection mappings'
        : isConfiguratorDataImport
            ? configuratorAssignmentMode === 'update_and_assign' ? 'Update and assign products' : 'Update variant attributes'
            : csvXlsxImportMode === 'replace_matched_skus' ? 'Submit and replace fitments' : 'Submit file';
    const csvModeLabel = isCollectionMappingImport
        ? 'Upsert mappings'
        : isConfiguratorDataImport
            ? configuratorAssignmentMode === 'update_and_assign' ? 'Update attributes and assign' : 'Update attributes only'
            : csvXlsxImportMode === 'replace_matched_skus' ? 'Replace matched SKUs' : 'Append';

    return (
        <>
            <s-page heading={pageTitle} inlineSize="large">
                {!isLegacy && <>
                    <s-button slot="secondary-actions" href="/storefront?section=reference-data">Manage data</s-button>
                    <s-button slot="secondary-actions" icon={pageView === 'exports' ? 'upload' : 'download'}
                        onClick={() => handlePageViewChange(pageView === 'exports' ? 'imports' : 'exports')}>
                        {pageView === 'exports' ? 'Imports' : 'Exports'}
                    </s-button>
                </>}
                <s-stack gap="base">
                    {/* The s-page heading shows only in the admin title bar; repeat it on the page. */}
                    <s-heading fontSize="large-200">{pageTitle}</s-heading>
                    {pageView === 'imports' ? importSection : exportSection}
                    {historySection}
                </s-stack>
            </s-page>

            <s-modal ref={undoModalRef} id="pf-undo-tag-import-modal" heading="Undo product-tag import?">
                {productTagUndoTarget && (
                    <s-stack gap="base">
                        {productTagUndoError && <s-banner tone="critical">{productTagUndoError}</s-banner>}
                        <s-paragraph>Fitment mappings created by {productTagUndoTarget.originalFileName || 'this import'} will be removed.</s-paragraph>
                    </s-stack>
                )}
                <s-button slot="primary-action" variant="primary" tone="critical" loading={productTagUndoBusy} onClick={confirmUndoProductTags}>
                    Undo import
                </s-button>
                <s-button slot="secondary-actions" disabled={productTagUndoBusy} onClick={() => setProductTagUndoTarget(undefined)}>Cancel</s-button>
            </s-modal>

            <s-modal ref={csvPreviewModalRef} id="pf-csv-preview-modal" size="large" heading={csvPreviewTitle}>
                {csvPreviewModalOpen && csvPreview && (
                    <s-stack gap="base">
                        {importQueueFull && <s-banner tone="info" heading="Import queue full">{importCapacityMessage}</s-banner>}

                        {fitmentHeaderPreview && (
                            <s-stack gap="small-200">
                                {fitmentHeaderPreview.headerRowNumber && (
                                    <s-text>
                                        Column headings found on row {fitmentHeaderPreview.headerRowNumber}. First {fitmentHeaderPreview.headerRowNumber - 1} rows skipped.
                                    </s-text>
                                )}
                                <s-select
                                    label="Column heading row"
                                    value={customHeaderRow ? 'custom' : String(fitmentHeaderPreview.headerRowNumber ?? '')}
                                    disabled={isPreviewingCsv || isSubmitting}
                                    onChange={(event) => {
                                        const value = fieldValue(event);
                                        if (value === 'custom') { setCustomHeaderRow(true); setHeaderRowInput(''); }
                                        else void handlePreviewCsvXlsx(Number(value));
                                    }}
                                >
                                    {headerRowOptions.map(option => (
                                        <s-option key={option.value} value={option.value} disabled={'disabled' in option && option.disabled}>{option.label}</s-option>
                                    ))}
                                </s-select>
                                {customHeaderRow && (
                                    <s-grid gridTemplateColumns="1fr auto" gap="small-200" alignItems="end">
                                        <s-number-field label="Heading row number" min={1} autocomplete="off"
                                            value={headerRowInput} disabled={isPreviewingCsv || isSubmitting}
                                            onInput={(event) => setHeaderRowInput(fieldValue(event))} />
                                        <s-button onClick={() => { void handlePreviewCsvXlsx(Number(headerRowInput)); }}
                                            disabled={isPreviewingCsv || isSubmitting || !Number.isSafeInteger(Number(headerRowInput)) || Number(headerRowInput) < 1}>
                                            Update preview
                                        </s-button>
                                    </s-grid>
                                )}
                                {isPreviewingCsv && <s-text color="subdued">Refreshing column mapping and sample rows…</s-text>}
                            </s-stack>
                        )}

                        <s-stack direction="inline" gap="small-200 base">
                            <s-text color="subdued">File: {csvPreview.fileName || uploadState.file?.name || 'Selected file'}</s-text>
                            <s-text color="subdued">Rows scanned: {csvPreview.scannedDataRowCount.toLocaleString()}</s-text>
                            <s-text color="subdued">Mode: {csvModeLabel}</s-text>
                        </s-stack>

                        {isCollectionMappingImport ? (
                            <s-banner tone="info" heading="Existing mappings may be reassigned">
                                A vehicle already mapped to another collection moves to the collection in its row. Mappings missing from the file stay as they are.
                            </s-banner>
                        ) : !isConfiguratorDataImport && csvXlsxImportMode === 'replace_matched_skus' ? (
                            <s-banner tone="warning" heading="Replace mode selected">
                                Existing fitments are replaced for matched identifiers with vehicle rows. Rows containing only an identifier and Universal preserve existing fitments.
                            </s-banner>
                        ) : null}

                        {csvPreview.errors?.length ? (
                            <s-banner tone="critical" heading="Preview errors">
                                <s-unordered-list>
                                    {csvPreview.errors.map((error) => <s-list-item key={error}>{error}</s-list-item>)}
                                </s-unordered-list>
                            </s-banner>
                        ) : null}

                        {csvPreview.warnings?.length ? (
                            <s-banner tone="info" heading="Preview notes">
                                <s-unordered-list>
                                    {csvPreview.warnings.map((warning) => <s-list-item key={warning}>{warning}</s-list-item>)}
                                </s-unordered-list>
                            </s-banner>
                        ) : null}

                        <s-stack gap="small-200">
                            <s-heading>Column mapping</s-heading>
                            <PreviewTable headings={['Import field', 'File column', 'Requirement']} rows={csvColumnMappingRows} />
                        </s-stack>

                        {csvPreviewRows.length > 0 && (
                            <s-stack gap="small-200">
                                <s-heading>Sample rows</s-heading>
                                <PreviewTable headings={csvPreviewHeadings} rows={csvPreviewRows} />
                                {csvPreview.hasMoreRows && <s-text color="subdued">Showing the first {csvPreview.rows.length} data rows.</s-text>}
                            </s-stack>
                        )}
                    </s-stack>
                )}
                <s-button slot="primary-action" variant="primary" onClick={handleSubmit} loading={isSubmitting}
                    disabled={importSubmissionDisabled || !csvPreviewCanSubmit || isPreviewingCsv}>
                    {csvSubmitLabel}
                </s-button>
                <s-button slot="secondary-actions" onClick={handleCloseCsvPreviewModal}>Back to upload</s-button>
                <s-button slot="secondary-actions" tone="critical" onClick={handleCancelCsvImport}>Cancel import</s-button>
            </s-modal>

            <s-modal ref={acesModalRef} id="pf-aces-setup-modal" heading="Finalize ACES import">
                {acesSetup && (
                    <s-stack gap="base">
                        <s-text color="subdued">
                            {acesSetup.details.documentTitle || 'Untitled ACES document'}{acesSetup.details.company ? ` · ${acesSetup.details.company}` : ''}
                        </s-text>
                        <s-select
                            label="Vendor (optional)"
                            value={acesSetup.selectedVendor || noVendorOptionValue}
                            onChange={(event) => {
                                const value = fieldValue(event);
                                setAcesSetup((previous) => {
                                    if (!previous) {
                                        return previous;
                                    }

                                    const selectedVendor = value === noVendorOptionValue ? '' : value;

                                    return {
                                        ...previous,
                                        selectedVendor,
                                        skuSuffixRule: selectedVendor ? (vendorSuffixRulesByVendor[selectedVendor]?.skuPrefix ?? '') : '',
                                        useSkuSuffixRule: selectedVendor ? previous.useSkuSuffixRule : false,
                                        updateVendorRule: selectedVendor ? previous.updateVendorRule : false,
                                        normalizeSku: selectedVendor ? (vendorSuffixRulesByVendor[selectedVendor]?.normalizeSku ?? false) : false,
                                    };
                                });
                            }}
                        >
                            <s-option value={noVendorOptionValue}>No vendor (match store-wide)</s-option>
                            {acesSetup.details.vendorOptions.map((vendor) => <s-option key={vendor} value={vendor}>{vendor}</s-option>)}
                        </s-select>
                        <s-text-field
                            label="Vendor SKU suffix rule"
                            value={acesSetup.skuSuffixRule}
                            autocomplete="off"
                            disabled={!acesSetup.selectedVendor}
                            onInput={(event) => {
                                const value = fieldValue(event);
                                setAcesSetup((previous) => previous ? { ...previous, skuSuffixRule: value } : previous);
                            }}
                        />
                        <s-checkbox
                            label="Use this suffix rule for this import"
                            checked={acesSetup.useSkuSuffixRule}
                            disabled={!acesSetup.selectedVendor}
                            onChange={(event) => {
                                const checked = fieldChecked(event);
                                setAcesSetup((previous) => previous ? { ...previous, useSkuSuffixRule: checked } : previous);
                            }}
                        />
                        <s-checkbox
                            label="Normalize SKU (remove special characters before matching)"
                            checked={acesSetup.normalizeSku}
                            disabled={!acesSetup.selectedVendor}
                            onChange={(event) => {
                                const checked = fieldChecked(event);
                                setAcesSetup((previous) => previous ? { ...previous, normalizeSku: checked } : previous);
                            }}
                        />
                        <s-checkbox
                            label="Update saved vendor rule with this value"
                            checked={acesSetup.updateVendorRule}
                            disabled={!acesSetup.selectedVendor}
                            onChange={(event) => {
                                const checked = fieldChecked(event);
                                setAcesSetup((previous) => previous ? { ...previous, updateVendorRule: checked } : previous);
                            }}
                        />
                    </s-stack>
                )}
                <s-button slot="primary-action" variant="primary" onClick={handleConfirmAcesSetup} loading={isConfirmingAcesSetup}>Start ACES import</s-button>
                <s-button slot="secondary-actions" disabled={isConfirmingAcesSetup} onClick={handleCloseAcesModal}>Cancel</s-button>
            </s-modal>

            {productTagPreview && <ProductTagReviewModal
                key={productTagPreview.queueId}
                preview={productTagPreview}
                initialConfirmDiscard={confirmTagDiscard}
                busy={tagPreviewBusy}
                error={productTagPreviewError}
                onApprove={handleApproveProductTags}
                onDiscard={handleDiscardProductTags}
                onClose={() => { if (!tagActionBusy.current) { setProductTagPreview(undefined); setProductTagPreviewError(undefined); } }}
            />}
        </>
    );
};

export default ImportsPage;
